/**
 * Pitching/catching protection for baseball fielding randomize:
 * ordered rows of { playerId, innings } map to consecutive innings (1..n).
 */

export type BatteryScheduleRow = { playerId: string; innings: number };

export function deserializeBatterySchedule(
  protectedPositions: { [inn: number]: { [pos: string]: string } } | undefined,
  position: 'P' | 'C',
  totalInnings: number
): BatteryScheduleRow[] {
  if (!protectedPositions || totalInnings < 1) return [];
  const rows: BatteryScheduleRow[] = [];
  let inn = 1;
  while (inn <= totalInnings) {
    const pid = protectedPositions[inn]?.[position];
    if (!pid) {
      inn++;
      continue;
    }
    let len = 0;
    let j = inn;
    while (j <= totalInnings && protectedPositions[j]?.[position] === pid) {
      len++;
      j++;
    }
    rows.push({ playerId: pid, innings: len });
    inn = j;
  }
  return rows;
}

/**
 * Rebuilds P/C on innings 1..totalInnings from ordered schedules; preserves other keys per inning (e.g. DH if present).
 */
export function mergeBatterySchedulesIntoProtected(
  base: { [inn: number]: { [pos: string]: string } } | undefined,
  pitchRows: BatteryScheduleRow[],
  catchRows: BatteryScheduleRow[],
  totalInnings: number
): { [inn: number]: { [pos: string]: string } } {
  const out: { [inn: number]: { [pos: string]: string } } = {};
  for (let i = 1; i <= totalInnings; i++) {
    const prev = { ...(base?.[i] || {}) };
    delete prev.P;
    delete prev.C;
    out[i] = prev;
  }

  let inn = 1;
  for (const row of pitchRows) {
    if (!row.playerId || row.innings < 1) continue;
    for (let k = 0; k < row.innings && inn <= totalInnings; k++) {
      if (!out[inn]) out[inn] = {};
      out[inn].P = row.playerId;
      inn++;
    }
  }

  inn = 1;
  for (const row of catchRows) {
    if (!row.playerId || row.innings < 1) continue;
    for (let k = 0; k < row.innings && inn <= totalInnings; k++) {
      if (!out[inn]) out[inn] = {};
      out[inn].C = row.playerId;
      inn++;
    }
  }

  return out;
}

export function batteryConflictsWithDh(
  protectedMerged: { [inn: number]: { [pos: string]: string } },
  dhId: string,
  totalInnings: number
): boolean {
  if (!dhId) return false;
  for (let i = 1; i <= totalInnings; i++) {
    const slot = protectedMerged[i];
    if (slot?.P === dhId || slot?.C === dhId) return true;
  }
  return false;
}
